import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { Country, State, City } from 'country-state-city';
import type { ICountry, IState, ICity } from 'country-state-city';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface Step2AddressProps {
  onComplete: (step: string) => void;
}

interface Address {
  street: string;
  number: string;
  apartment: string;
  city: string;
  cityCode: string;
  state: string;
  stateCode: string;
  zipCode: string;
  country: string;
  countryCode: string;
  additionalInfo: string;
}

const Step2Address: React.FC<Step2AddressProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState<Address>({
    street: '',
    number: '',
    apartment: '',
    city: '',
    cityCode: '',
    state: '',
    stateCode: '',
    zipCode: '',
    country: '',
    countryCode: '',
    additionalInfo: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);

  useEffect(() => {
    logger.info('Step2Address', 'Componente montado');
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    return () => logger.info('Step2Address', 'Componente desmontado');
  }, []);

  useEffect(() => {
    if (address.countryCode) {
      const countryStates = State.getStatesOfCountry(address.countryCode);
      setStates(countryStates);
      setAddress(prev => ({
        ...prev,
        state: '',
        stateCode: '',
        city: '',
        cityCode: ''
      }));
      setCities([]);
    }
  }, [address.countryCode]);

  useEffect(() => {
    if (address.countryCode && address.stateCode) {
      const stateCities = City.getCitiesOfState(address.countryCode, address.stateCode);
      setCities(stateCities);
      setAddress(prev => ({
        ...prev,
        city: '',
        cityCode: ''
      }));
    }
  }, [address.countryCode, address.stateCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    logger.info('Step2Address', `Cambio en ${name}`, value);
    if (name === 'country') {
      const selectedCountry = countries.find(c => c.isoCode === value);
      if (selectedCountry) {
        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
        setStates(countryStates);
        setAddress(prev => ({
          ...prev,
          state: '',
          stateCode: '',
          city: '',
          cityCode: '',
          country: selectedCountry.name,
          countryCode: selectedCountry.isoCode
        }));
      }
    } else if (name === 'state') {
      const selectedState = states.find(s => s.isoCode === value);
      if (selectedState) {
        setAddress(prev => ({
          ...prev,
          state: selectedState.name,
          stateCode: selectedState.isoCode,
          city: '',
          cityCode: ''
        }));
      }
    } else if (name === 'city') {
      const selectedCity = cities.find(c => c.name === value);
      if (selectedCity) {
        setAddress(prev => ({
          ...prev,
          city: selectedCity.name,
          cityCode: selectedCity.name // La API no proporciona códigos de ciudad, usamos el nombre
        }));
      }
    } else {
      setAddress(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    logger.info('Step2Address', 'Enviando formulario', address);
    try {
      if (!address.street || !address.city || !address.state || !address.zipCode || !address.country) {
        throw new Error('Por favor, completa todos los campos requeridos');
      }
      if (!auth.currentUser) {
        setError('Usuario no autenticado');
        setLoading(false);
        logger.error('Step2Address', 'Usuario no autenticado');
        return;
      }
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        address: {
          ...address,
          updatedAt: new Date().toISOString()
        }
      });
      logger.info('Step2Address', 'Onboarding de dirección completado');
      onComplete('address');
    } catch (error: any) {
      logger.error('Step2Address', 'Error en el proceso', error);
      setError(error.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const inputBase = "w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/10";
  const selectBase = "w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/10 cursor-pointer appearance-none";

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">{t('onboarding.address.title')}</h2>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        {t('onboarding.address.description')}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-2xl mx-auto">
        <div className="flex flex-col gap-2">
          <label htmlFor="country" className="text-gray-700 font-medium">{t('onboarding.address.country')}</label>
          <select
            id="country"
            name="country"
            value={address.countryCode}
            onChange={handleChange}
            required
            className={selectBase}
          >
            <option value="">{t('onboarding.address.selectCountry')}</option>
            {countries.map(country => (
              <option key={country.isoCode} value={country.isoCode}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="state" className="text-gray-700 font-medium">{t('onboarding.address.state')}</label>
          <select
            id="state"
            name="state"
            value={address.stateCode}
            onChange={handleChange}
            required
            className={`${selectBase} ${!address.countryCode ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!address.countryCode}
          >
            <option value="">{t('onboarding.address.selectState')}</option>
            {states.map(state => (
              <option key={state.isoCode} value={state.isoCode}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="city" className="text-gray-700 font-medium">{t('onboarding.address.city')}</label>
          <select
            id="city"
            name="city"
            value={address.city}
            onChange={handleChange}
            required
            className={`${selectBase} ${!address.stateCode ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!address.stateCode}
          >
            <option value="">{t('onboarding.address.selectCity')}</option>
            {cities.map(city => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="street" className="text-gray-700 font-medium">{t('onboarding.address.street')}</label>
          <input
            type="text"
            id="street"
            name="street"
            value={address.street}
            onChange={handleChange}
            placeholder={t('onboarding.address.streetPlaceholder')}
            required
            className={inputBase}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="number" className="text-gray-700 font-medium">{t('onboarding.address.number')}</label>
            <input
              type="text"
              id="number"
              name="number"
              value={address.number}
              onChange={handleChange}
              placeholder={t('onboarding.address.numberPlaceholder')}
              required
              className={inputBase}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="apartment" className="text-gray-700 font-medium">{t('onboarding.address.apartment')}</label>
            <input
              type="text"
              id="apartment"
              name="apartment"
              value={address.apartment}
              onChange={handleChange}
              placeholder={t('onboarding.address.apartmentPlaceholder')}
              className={inputBase}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="zipCode" className="text-gray-700 font-medium">{t('onboarding.address.zipCode')}</label>
          <input
            type="text"
            id="zipCode"
            name="zipCode"
            value={address.zipCode}
            onChange={handleChange}
            placeholder={t('onboarding.address.zipCodePlaceholder')}
            required
            className={inputBase}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="additionalInfo" className="text-gray-700 font-medium">{t('onboarding.address.additionalInfo')}</label>
          <input
            type="text"
            id="additionalInfo"
            name="additionalInfo"
            value={address.additionalInfo}
            onChange={handleChange}
            placeholder={t('onboarding.address.additionalInfoPlaceholder')}
            className={inputBase}
          />
        </div>
        {error && <div className="text-red-600 bg-red-100 border border-red-200 rounded-lg px-4 py-3 text-center">{t(error)}</div>}
        <button 
          type="submit" 
          disabled={loading}
          className="w-full p-4 bg-red-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? t('common.saving') : t('common.saveAndContinue')}
        </button>
      </form>
    </div>
  );
};

export default Step2Address; 